import { durationSm } from "@porsche-design-system/components-react/emotion"
import type { ComponentType, PropsWithChildren } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ModalContainer } from "./ModalContainer"
import type { ModalType } from "./ModalContext"
import { ModalContext } from "./ModalContext"

// pds modal reference: https://github.com/porsche-design-system/porsche-design-system/blob/main/packages/components/src/components/common/dialog-base/dialog-base-styles.ts#L172
const closingDuration = Number.parseFloat(durationSm) * 1000 + 100 // with some buffer for state updates, effects and rerenders

interface ModalProviderProps extends PropsWithChildren {
	/**
	 * Root element for modal nodes
	 */
	root?: Element
}

export interface ModalProps<Props = object> {
	id: string
	modalProps: Props
}

/**
 * Modal Provider
 *
 * Provides modal context and renders ModalRoot.
 */
export const ModalProvider = ({ children, root }: ModalProviderProps) => {
	const [modals, setModals] = useState<Record<string, ModalType<any>>>({})

	// tracks in-flight open/close timers so they can be cancelled on unmount, avoiding setState calls (and test env crashes) after teardown
	const pendingTimeouts = useRef(new Set<ReturnType<typeof setTimeout>>())

	useEffect(() => {
		const timeouts = pendingTimeouts.current
		return () => {
			for (const timeoutId of timeouts) {
				clearTimeout(timeoutId)
			}
			timeouts.clear()
		}
	}, [])

	const showModal = useCallback(
		(id: string, component: ComponentType<any>, props: any) => {
			setModals((prevModals) => ({
				...prevModals,
				[id]: {
					component,
					isOpen: false,
					props,
				},
			}))
			const timeoutId = setTimeout(() => {
				pendingTimeouts.current.delete(timeoutId)
				setModals((prevModals) => {
					const newModals = { ...prevModals }
					newModals[id].isOpen = true
					return newModals
				})
			}, 20)
			pendingTimeouts.current.add(timeoutId)
		},
		[setModals],
	)

	const isOpen = useCallback((id: string) => modals[id].isOpen, [modals])

	const hideModal = useCallback(
		(id: string) => {
			if (!isOpen(id)) {
				return
			}
			setModals((prevModals) => {
				const newModals = { ...prevModals }
				newModals[id].isOpen = false
				return newModals
			})
			const timeoutId = setTimeout(() => {
				pendingTimeouts.current.delete(timeoutId)
				setModals((prevModals) => {
					const newModals = { ...prevModals }
					delete newModals[id]
					return newModals
				})
			}, closingDuration)
			pendingTimeouts.current.add(timeoutId)
		},
		[setModals, isOpen],
	)

	const contextValue = useMemo(() => ({ hideModal, isOpen, showModal }), [showModal, hideModal, isOpen])

	return (
		<ModalContext.Provider value={contextValue}>
			{children}
			<ModalContainer modals={modals} root={root} />
		</ModalContext.Provider>
	)
}
